import { Ellipsis, Pencil, Send, Trash } from 'lucide-react';
import type { AskAiBody } from 'bindings/ai/AskAiBody';
import type { Conversation } from 'bindings/ai/Conversation';
import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import { useRecoilState } from 'recoil';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';
import { AAiAgents, AiAgent, AiAgentButton, AiAgentWrapperDiv, deleteAiAgent, postAiAgent, putAiAgent, SIDELY_AI_AGENTS } from '../../atoms/global/ai_agents';
import { useMe } from '../../atoms/global/users';
import Dropdown2, { HoverPopup } from '../../components_v2/dropdown/Dropdown2';
import Input from '../../components_v2/input/Input';
import {
	PanelContext
} from '../../components_v2/panel/context';
import { PopupMode } from '../../components_v2/popup/model/Model';
import Popup from '../../components_v2/popup/Popup';
import PopupCreation, { PopupHeader } from '../../components_v2/popup/PopupCreation';
import { SortSetting } from '../../components_v2/sort/TableSort';
import { getLocalStorage, setLocalStorage } from '../../helpers/localStorage';
import AiSparklingsIcon from '../../styles/custom-icons/ai-sparklings';
import { AiAnimation } from '../../styles/global/css/GlobalButton';
import { AiGradiant, PrimaryColor, BorderColor, GreySidely3, RedSidely, SurfaceColor } from '../../styles/global/css/Utils';
import { Translate, translateToString } from '../../styles/global/translate';
import { NoMarginH3, NoMarginH5, NoMarginH6, NoMarginP } from '../authorization/users/Members';
import { creationPopupInputStyle } from '../../components_v2/popup/style/CreationFormStyle';
import { LoadingStateEnum } from '../import/model';
import { NullableReactStateType, ReactStateType } from '../map/Map';
import { BlueTextA } from '../map/modalRight/ModalCalendar';
import { Unhautorized } from '../noData/NoData';
import Restricted from '../permissions/Restricted';
import { FlexDiv } from '../products/style';
import { askToAi } from './actions';
import { AiDataFilters, useAiArchivesFetcher } from './aiArchives';

const DEFAULT_AI_ICON_SIZE = 30;

export type AiChatState = {
	conversationId: AiChat['conversationId'],
	archives: AskAiBody['archives'],
	suggestions: string[],
	chartId: AiChat['chartId'],
	dashboardId: AiChat['dashboardId'],
	lastTimeRefreshed: AiChat['lastTimeRefreshed'],
	filters: AiChat['filters'],
}

export type AiChatViewState = {
	chatHook: ReactStateType<AiChat[]>,
	aiStateHook: NullableReactStateType<AiChatState>,
}

export type AiChatPart = {
	conversationId: AiChat['conversationId'];
	localId: AiChat['localId'];
} & ({
	question: AiChat['question'];
	title: AiChat['title'];
	agentId: AiChat['agentId'];
	chartId: AiChat['chartId'];
	dashboardId: AiChat['dashboardId'];
	archives: AiChat['archives'];
	lastTimeRefreshed: AiChat['lastTimeRefreshed'];
	filters: AiChat['filters'];
} | {
	stream: AiChat['stream'];
});

export const AI_CHAT_HISTORY_LOCAL_STORAGE_KEY = 'AI_CHAT_HISTORY';

export type AiChatWrapperJson = {
	conversationId: AiChat['conversationId'];
	title: AiChat['title'];
	chats: AiChatJson[];
	chartId: AiChat['chartId'];
	dashboardId: AiChat['dashboardId'];
	agentId: AiChat['agentId'];
	createdAt: number;
	lastTimeUsed: number;
	lastTimeRefreshed: number;
	archives: AiChat['archives'];
	filters?: AiChat['filters'];
}

type AiChatJson = {
	localId: string;
	question: string;
	answer: string;
};

export function aiChatsFromJson(json: AiChatWrapperJson): AiChat[] {
	return json.chats.map((chat) => {
		return AiChat.fromJson(json, chat);
	});
}

class AiChat {
	question: string;
	answer: string = '';
	stream?: ReadableStream<Uint8Array>;
	conversationId?: string;
	localId: string;
	onUpdate?: () => void;
	loadingState: LoadingStateEnum = LoadingStateEnum.LOADING;
	error: boolean = false;
	title: string | undefined;
	agentId: number | undefined;
	chartId: string | undefined;
	dashboardId: string | undefined;
	archives: string[] = [];
	lastTimeRefreshed: number = Date.now();
	filters: AiDataFilters | undefined;

	static fromJson(wrapper: AiChatWrapperJson, json: AiChatJson): AiChat {
		const chat = new AiChat({
			localId: json.localId,
			question: json.question,
			conversationId: wrapper.conversationId,
			title: wrapper.title,
			agentId: wrapper.agentId,
			chartId: wrapper.chartId,
			dashboardId: wrapper.dashboardId,
			archives: wrapper.archives,
			lastTimeRefreshed: wrapper.lastTimeRefreshed,
			filters: wrapper.filters,
		});
		chat.loadingState = LoadingStateEnum.LOADED;
		chat.answer = json.answer;
		return chat;
	}

	toJson() {
		return {
			localId: this.localId,
			question: this.question,
			answer: this.answer,
		};
	}

	innerOnUpdate() {
		this.onUpdate?.();
		// save in local storage
		const old = getLocalStorage<AiChatWrapperJson[]>(AI_CHAT_HISTORY_LOCAL_STORAGE_KEY) ?? [];
		const arrayIndex = old.findIndex((e) => e.conversationId == this.conversationId);
		const obj = { localId: this.localId, question: this.question, answer: this.answer };
		if (arrayIndex === -1) {
			old.push({
				chats: [obj],
				lastTimeUsed: Date.now(),
				createdAt: Date.now(),
				lastTimeRefreshed: this.lastTimeRefreshed,
				conversationId: this.conversationId,
				title: this.title,
				agentId: this.agentId,
				chartId: this.chartId,
				dashboardId: this.dashboardId,
				archives: this.archives,
				filters: this.filters,
			});
		} else {
			old[arrayIndex].lastTimeUsed = Date.now();
			old[arrayIndex].lastTimeRefreshed = this.lastTimeRefreshed;
			old[arrayIndex].archives = this.archives;
			const chatIndex = old[arrayIndex].chats.findIndex((e) => e.localId == this.localId);
			if (chatIndex === -1) {
				old[arrayIndex].chats.push(obj);
			} else {
				old[arrayIndex].chats[chatIndex] = obj;
			}
		}
		setLocalStorage(AI_CHAT_HISTORY_LOCAL_STORAGE_KEY, old);
	}

	constructor(part: AiChatPart) {
		if ('archives' in part) {
			this.archives = part.archives;
			this.chartId = part.chartId;
			this.dashboardId = part.dashboardId;
			this.lastTimeRefreshed = part.lastTimeRefreshed;
			this.filters = part.filters;
			this.title = part.title;
			this.agentId = part.agentId;
			this.question = part.question;
		} else {
			this.question = '';
		}
		this.localId = part.localId;
		this.conversationId = part.conversationId;
		if ('stream' in part) {
			this.stream = part.stream;
			this.readStream();
		}
	}

	addPart(part: AiChatPart): boolean {
		if (this.localId !== part.localId) return false;
		if (!this.conversationId) this.conversationId = part.conversationId;

		if ('question' in part) {
			this.question = part.question;
			this.innerOnUpdate(); // question change → re-render
		}

		if ('stream' in part) {
			this.stream = part.stream;
			this.readStream();
		}
		return true;
	}

	async readStream() {
		if (!this.stream || this.stream?.locked) return;
		this.loadingState = LoadingStateEnum.LOADING;
		this.innerOnUpdate();

		let reader: ReadableStreamDefaultReader<Uint8Array>;
		try {
			reader = this.stream.getReader();
		} catch (_e) {
			return;
		}
		const decoder = new TextDecoder();
		let result = await reader.read();

		while (!result.done) {
			this.answer += decoder.decode(result.value);
			this.innerOnUpdate();
			result = await reader.read();
		}

		reader.releaseLock();
		this.loadingState = LoadingStateEnum.LOADED;
		this.innerOnUpdate();
	}
}

function QuestionAndAnswer(props: { chat: AiChat, isLast: boolean, wrapperHeight: number | undefined }) {
	const [questionHeight, setQuestionHeight] = React.useState(0);
	const { chat, isLast } = props;
	return <>
		<Answer isLast={isLast} questionHeight={questionHeight} wrapperHeight={props.wrapperHeight}><ReactMarkdown remarkPlugins={[remarkGfm]}>{chat.answer}</ReactMarkdown></Answer>
		<FlexDiv gap='10px'><AiIcon error={chat.loadingState === LoadingStateEnum.ERROR} />{chat.loadingState ? <ThinkingAnimation opacity='0.7' loadingState={chat.loadingState} /> : <Translate id='thinking_finished' />}</FlexDiv>
		{chat.question && <Question innerRef={React.useCallback(node => setQuestionHeight(node?.offsetHeight ?? 0), [])}>{chat.question}</Question>}
	</>;
}

const MAX_AGENT_LISTED = 4;
const DATA_STALE_AFTER_MS = 60 * 60 * 1000;

export default function AiChatView() {
	const context = React.useContext(PanelContext);
	const user = useMe();
	const [aiAgents, setAiAgents] = useRecoilState(AAiAgents);
	const [aiState, setAiState] = context.aiStateHook;
	const fetchAiArchives = useAiArchivesFetcher();
	const [refreshState, setRefreshState] = React.useState<LoadingStateEnum>(LoadingStateEnum.LOADED);
	const { conversationId, suggestions, chartId, dashboardId, lastTimeRefreshed } = aiState ?? {};
	const [aiChats, setAiChats] = context.aiChatHook;
	const [title] = context.titleHook;
	const [inputValue, setInputValue] = React.useState<string>('');
	const wrapperRef = React.useRef<HTMLDivElement>();
	const chatWrapperRef = React.useRef<HTMLDivElement>();
	const conversationChats = React.useMemo(() => {
		if (aiChats.length === 0) return [];
		const lastConversationId = conversationId ?? (aiChats.length > 0 ? aiChats[aiChats.length - 1]?.conversationId : undefined);
		return aiChats.filter(chat => chat.conversationId === lastConversationId).reverse();
	}, [aiChats, conversationId]);
	const [isAgentCreationOpen, setIsAgentCreationOpen] = React.useState<AiAgent | boolean>(false);
	const isAgentDropdownOpenHook = React.useState(false);
	const [_, setIsAgentDropdownOpen] = isAgentDropdownOpenHook;
	const [selectedAiAgent, setSelectedAiAgent] = React.useState<AiAgent | undefined>(undefined);
	React.useEffect(() => {
		const agentId = aiChats.find(chat => chat.conversationId === conversationId && chat.agentId !== undefined)?.agentId;
		setSelectedAiAgent(aiAgents.find(agent => agent.id === agentId));
	}, [aiChats, conversationId, aiAgents]);

	const isConversationDropdownOpenHook = React.useState(false);

	const emptyChat = conversationChats.length === 0;

	const canRefresh = chartId !== undefined || dashboardId !== undefined;

	// Re-render when the data becomes stale so the banner shows up without any other update
	const [isDataStale, setIsDataStale] = React.useState(false);
	React.useEffect(() => {
		if (lastTimeRefreshed === undefined) return setIsDataStale(false);
		const staleIn = lastTimeRefreshed + DATA_STALE_AFTER_MS - Date.now();
		setIsDataStale(staleIn <= 0);
		if (staleIn <= 0) return;
		const timeout = setTimeout(() => setIsDataStale(true), staleIn);
		return () => clearTimeout(timeout);
	}, [lastTimeRefreshed]);

	async function refreshData() {
		if (!aiState || refreshState === LoadingStateEnum.LOADING) return;
		setRefreshState(LoadingStateEnum.LOADING);
		try {
			const archives = await fetchAiArchives({ chartId, dashboardId, filters: aiState.filters });
			if (!archives) throw new Error('chart or dashboard not found');
			const now = Date.now();
			setAiState(state => state && state.conversationId === aiState.conversationId ? { ...state, archives, lastTimeRefreshed: now } : state);
			const history = getLocalStorage<AiChatWrapperJson[]>(AI_CHAT_HISTORY_LOCAL_STORAGE_KEY) ?? [];
			const conversation = history.find(c => c.conversationId === aiState.conversationId);
			if (conversation) {
				conversation.archives = archives;
				conversation.lastTimeRefreshed = now;
				setLocalStorage(AI_CHAT_HISTORY_LOCAL_STORAGE_KEY, history);
			}
			setRefreshState(LoadingStateEnum.LOADED);
		} catch (_e) {
			setRefreshState(LoadingStateEnum.ERROR);
		}
	}

	function sendAiQuestion(input?: string) {
		const localId = new Date().getTime().toString();
		let index = -1;
		const question = (input ?? inputValue).trim();
		setAiChats(prevChats => {
			index = prevChats.length;
			return [...prevChats, new AiChat({
				title,
				agentId: selectedAiAgent?.id,
				question,
				localId,
				conversationId,
				chartId,
				dashboardId,
				archives: aiState?.archives ?? [],
				lastTimeRefreshed: lastTimeRefreshed ?? Date.now(),
				filters: aiState?.filters,
			})];
		});

		const archives = [...aiState?.archives ?? []];
		if (selectedAiAgent) {
			archives.push(selectedAiAgent.body);
		}
		chatWrapperRef?.current?.scrollTo({ top: 0, behavior: 'smooth' });
		askToAi({
			interpret: true,
			question,
			archives,
			products: null,
			config: {
				agent_id: null,
				provider: 'openai',
				api_key: null
			},
			fields: null,
			form_uuid: null,
			company_uuid: null,
			last_fields: null,
			form_id: null,
			images: [],
			past_conversations: conversationChats.reduce<Conversation[]>((acc, chat) => {
				if (chat.error) return acc;
				if (chat.question) {
					acc.push({ 'User': chat.question });
				}
				if (chat.answer) {
					acc.push({ 'Ai': chat.answer });
				}
				return acc;
			}, [])
		}).then(stream => {
			setAiChats(chats => {
				if (index === -1) return chats;
				const aiChats = [...chats];
				aiChats[index].onUpdate = () => setAiChats(chats => [...chats]);
				aiChats[index].addPart({
					localId,
					stream,
					conversationId
				});
				return aiChats;
			});
		}).catch(e => {
			if (e !== 402) return;
			setAiChats(chats => {
				if (index === -1) return chats;
				const aiChats = [...chats];
				aiChats[index].loadingState = LoadingStateEnum.ERROR;
				aiChats[index].answer = translateToString('no_more_token');
				aiChats[index].error = true;
				return aiChats;
			});
		});
		setInputValue('');
	}

	const setChatWrapperRef = React.useCallback(node => chatWrapperRef.current = node, []);
	function agentDropdownOption(agent: AiAgent) {
		return <SortSetting
			justifyContent={'space-between'}
			key={agent.id} hoverColor={PrimaryColor} nowrap onClick={() => {
				setSelectedAiAgent(agent);
				setIsAgentDropdownOpen(false);
				setIsAgentDropdownOpen(false);
			}}>
			{agent.name}
			{agent.id >= 0 && <>
				<FlexDiv flexShrink={0}>
					<SortSetting hoverColor={PrimaryColor} onClick={(e) => {
						e.stopPropagation();
						setIsAgentCreationOpen(agent);
					}} fitContent >
						<Pencil size={16} />
					</SortSetting>
					<SortSetting hoverColor={RedSidely} onClick={(e) => {
						e.stopPropagation();
						if (agent.id === selectedAiAgent?.id) setSelectedAiAgent(undefined);
						deleteAiAgent(agent.id, setAiAgents);
					}} fitContent >
						<Trash size={16} />
					</SortSetting>
				</FlexDiv>
			</>}
		</SortSetting>;
	}

	return <div style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
		<PopupHeader
			title={`Sidely AI - ${title}`}
			onClose={() => context.setIsPanelOpen(false)}
			trailing={
				<Dropdown2
					isOpenState={isConversationDropdownOpenHook}
					button={({ setIsOpen }) => <AiAgentWrapperDiv onClick={() => setIsOpen((t) => !t)}>Conversations</AiAgentWrapperDiv>}
				>
					<ConversationDropdownPopup isOpenState={isConversationDropdownOpenHook} />
				</Dropdown2>}
		/>
		<Restricted to={{ objectAction: 'ReadAi' }} fallback={<Unhautorized />}>
			{lastTimeRefreshed !== undefined && isDataStale && <RefreshBanner>
				<Translate id='ai_data_last_refreshed' options={{ variables: [['date', new Date(lastTimeRefreshed).toLocaleString()]] }} />
				{canRefresh && (refreshState === LoadingStateEnum.LOADING
					? <ThinkingAnimation opacity='0.7' loadingState={refreshState} />
					: <BlueTextA noFullWidth onClick={refreshData}>
						<Translate id={refreshState === LoadingStateEnum.ERROR ? 'refresh_data_error' : 'refresh_data'} />
					</BlueTextA>)}
			</RefreshBanner>}
			<AiWrapper innerRef={React.useCallback(node => wrapperRef.current = node, [])}>
				{!emptyChat && <ChatWrapper innerRef={setChatWrapperRef} hasAgent={selectedAiAgent !== undefined}>
					{conversationChats.map((chat, index) => <QuestionAndAnswer key={chat.localId} chat={chat} isLast={index === 0} wrapperHeight={wrapperRef.current?.clientHeight} />)}
				</ChatWrapper>}
				<InputWrapper width={wrapperRef.current?.clientWidth} emptyChat={emptyChat}>
					{emptyChat && <FlexDiv flow='column' align='stretch' gap='10px'>
						<NoMarginP>👋 <Translate id='ai_greeting' options={{ variables: [['name', user.name.split(' ')[0]]] }} /></NoMarginP>
						<NoMarginH3><Translate id='ai_how_can_i_help' /></NoMarginH3>
						<FlexDiv margin='15px 0 0 0' justify='space-between'>
							<NoMarginH5><Translate id='ai_ask_your_agents' /></NoMarginH5>
							<BlueTextA noFullWidth onClick={() => setIsAgentCreationOpen(true)}>+ <Translate id='ai_agent_create' /></BlueTextA>
						</FlexDiv>
						<FlexDiv flexWrap='wrap' gap='10px'>
							{[...SIDELY_AI_AGENTS, ...aiAgents].sort((a, b) => b.id - a.id).slice(0, MAX_AGENT_LISTED).map(agent => <HoverPopup key={agent.id} button={<AiAgentButton agent={agent} onClick={setSelectedAiAgent} />}>
								{agent.description ? <>
									<NoMarginH6 style={{ whiteSpace: 'nowrap' }}>{agent.name}</NoMarginH6>
									<NoMarginP>{agent.description}</NoMarginP>
								</> : undefined}
							</HoverPopup>)}
							<Dropdown2
								isOpenState={isAgentDropdownOpenHook}
								button={({ setIsOpen }) => <AiAgentWrapperDiv onClick={() => setIsOpen(t => !t)}><Ellipsis strokeWidth={2.5} color={GreySidely3} /></AiAgentWrapperDiv>}
							>
								<>
									<SortSetting nowrap noHoverable>
										<NoMarginH5><Translate id='ai_sidely_agents' /></NoMarginH5>
									</SortSetting>
									{SIDELY_AI_AGENTS.map(agentDropdownOption)}
									<SortSetting nowrap noHoverable>
										<NoMarginH5><Translate id='ai_admin_agents' /></NoMarginH5>
									</SortSetting>
									{aiAgents.map(agentDropdownOption)}
									<SortSetting hoverColor={PrimaryColor} nowrap>
										<BlueTextA noFullWidth onClick={() => setIsAgentCreationOpen(true)}>+ <Translate id='ai_agent_create' /></BlueTextA>
									</SortSetting>
								</>
							</Dropdown2>
						</FlexDiv>
					</FlexDiv>}
					<div>
						{!!selectedAiAgent && <AiAgentButton
							deleteMode={emptyChat}
							agent={selectedAiAgent}
							onClick={() => emptyChat ? setSelectedAiAgent(undefined) : undefined}
							style={{ marginBottom: '-1px', borderBottom: `1px solid ${SurfaceColor}`, borderRadius: '5px 5px 0 0', zIndex: 1, position: 'relative' }}
						/>}
						<InputAndButtonWrapper agentSelected={!!selectedAiAgent}>
							<Input
								name='chat-input'
								type='text'
								value={inputValue}
								placeholder={translateToString('ai_question_placeholder')}
								onChange={setInputValue}
								textArea
								inputStyle={{
									width: '100%',
									containerWidth: '100%',
									border: 'none'
								}}
							/>
							<SendButton
								disabled={conversationChats.some(chat => chat.loadingState === LoadingStateEnum.LOADING) || (inputValue?.trim().length === 0 && !selectedAiAgent)}
								onClick={() => sendAiQuestion()}
							>
								<Send color='white' />
							</SendButton>
						</InputAndButtonWrapper>
					</div>
					{emptyChat && (suggestions?.length ?? 0) > 0 && <SuggestionsWrapper>
						<h4><Translate id='suggested_questions' /></h4>
						{suggestions?.map(suggestion => (
							<Suggestion key={suggestion} onClick={() => sendAiQuestion(suggestion)}>
								{suggestion}
							</Suggestion>
						))}

					</SuggestionsWrapper>}

				</InputWrapper>
			</AiWrapper>
		</Restricted>
		<Popup popupMode={PopupMode.Centered} isOpen={!!isAgentCreationOpen} onClickOut={() => setIsAgentCreationOpen(false)}>
			<AgentPopup onClose={() => setIsAgentCreationOpen(false)} agent={typeof isAgentCreationOpen === 'boolean' ? undefined : isAgentCreationOpen} />
		</Popup>
	</div>;
}

function ConversationDropdownPopup(props: { isOpenState: ReactStateType<boolean> }) {
	const context = React.useContext(PanelContext);
	const [__, setAiChats] = context.aiChatHook;
	const [aiState, setAiState] = context.aiStateHook;
	const [____, setTitle] = context.titleHook;
	const [_____, setPopupOpen] = props.isOpenState;
	const oldConversations = getLocalStorage<AiChatWrapperJson[]>(AI_CHAT_HISTORY_LOCAL_STORAGE_KEY) ?? [];
	const conversationId = aiState?.conversationId;

	function createNewConversation() {
		setPopupOpen(false);
		const newId = (conversationId ?? '') + ':' + Date.now();
		setAiChats(chats => chats.map(chat => {
			if (chat.conversationId === conversationId) {
				chat.conversationId = newId;
			}
			return chat;
		}));
		const updatedConversations = oldConversations.map(conversation => {
			if (conversation.conversationId === conversationId) {
				conversation.conversationId = newId;
			}
			return conversation;
		});
		localStorage.setItem(AI_CHAT_HISTORY_LOCAL_STORAGE_KEY, JSON.stringify(updatedConversations));

	}

	function restoreOldConversation(conversation: AiChatWrapperJson) {
		setPopupOpen(false);
		setTitle(conversation.title);
		console.log('RESTORE OLD CONVERSATION', conversation);
		setAiState({
			conversationId: conversation.conversationId,
			chartId: conversation.chartId,
			dashboardId: conversation.dashboardId,
			archives: conversation.archives,
			lastTimeRefreshed: conversation.lastTimeRefreshed,
			filters: conversation.filters,
			suggestions: [],
		});
	}
	return <>
		<SortSetting nowrap noHoverable>
			<NoMarginH5>Anciennes conversations</NoMarginH5>
		</SortSetting>
		{oldConversations.length === 0 && <SortSetting nowrap noHoverable>No conversation found</SortSetting>}
		{oldConversations.map((conversation, index) => (
			<SortSetting nowrap key={index} onClick={() => restoreOldConversation(conversation)} selected={conversation.conversationId === conversationId}>
				{conversation.title ?? new Date(conversation.lastTimeUsed).toLocaleString()}
			</SortSetting>
		))}
		<SortSetting nowrap>
			<BlueTextA noFullWidth onClick={createNewConversation}>+ Nouvelle conversation</BlueTextA>
		</SortSetting>
	</>;
}

function AgentPopup(props: { onClose: () => void, agent?: AiAgent }) {
	const agent = props.agent;
	const [_, setAiAgent] = useRecoilState(AAiAgents);
	const [name, setName] = React.useState(agent?.name ?? '');
	const [description, setDescription] = React.useState(agent?.description ?? '');
	const [body, setBody] = React.useState(agent?.body ?? '');
	const [loadingState, setLoadingState] = React.useState<LoadingStateEnum>(LoadingStateEnum.LOADED);

	return <PopupCreation
		title={agent ? agent.name : translateToString('ai_agent_create')}
		messageButton={agent ? <Translate id='global.edit' /> : undefined}
		onClose={props.onClose}
		canValidate={name.trim() !== '' && body.trim() !== ''}
		onSubmit={() => {
			const bodyData = { name, body, description };
			if (agent) {
				return putAiAgent(agent.id, bodyData, setAiAgent)
					.then(props.onClose)
					.catch(() => setLoadingState(LoadingStateEnum.ERROR));
			}
			return postAiAgent(bodyData, setAiAgent)
				.then(props.onClose)
				.catch(() => setLoadingState(LoadingStateEnum.ERROR));
		}}
		loading={loadingState}
	>
		<FlexDiv height='100%' flow='column' gap='40px' align='stretch' padding='0 20px'>
			<Input value={name} name='name' type='text' inputStyle={creationPopupInputStyle} label={<Translate id='name' />} onChange={setName} />
			<Input value={description} name='description' type='text' inputStyle={creationPopupInputStyle} label={<Translate id='ai_agent_description_label' />} onChange={setDescription} placeholder={translateToString('ai_agent_description_placeholder')} />
			<Input value={body} name='prompt' type='text' inputStyle={{ ...creationPopupInputStyle, height: '100%', containerHeight: '100%' }} label={<Translate id='instructions' />} textArea onChange={setBody} placeholder={translateToString('ai_agent_instructions_placeholder')} />
		</FlexDiv>
	</PopupCreation>;
}

const SendButton = styled.button`
	background-color: ${PrimaryColor};
	color: white;
	border: none;
	border-radius: 50%;
	width: 25px;
	height: 25px;
	cursor: pointer;
	margin-bottom: 4px;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-right: 4px;
	align-self: flex-end;
`;

const InputWrapper = styled.div<{ width?: number, emptyChat: boolean }>`
	display: flex;
	flex-flow: column;
	gap: 10px;
	background-color: ${SurfaceColor};
	width: 100%;
	position: relative;
	transition: bottom 0.5s;
	${props => props.emptyChat ? `
	` : `
		bottom: 0px;
	`}
`;

const SuggestionsWrapper = styled.div`
	border: 1px solid ${BorderColor};
	border-radius: 5px;
	padding: 10px;
	display: flex;
	flex-flow: column;
`;

const Suggestion = styled.p`
	margin: 0;
	padding: 3px;
	cursor: pointer;
	&:hover {
		background-color: color-mix(in oklab, ${PrimaryColor} 20%, transparent);
		border-radius: 5px;
	}
`;

const InputAndButtonWrapper = styled.div<{ agentSelected: boolean }>`
	display: flex;
	gap: 5px;
	border: 1px solid ${BorderColor};
	border-radius: ${p => p.agentSelected ? '0 5px 5px 5px' : '5px'};
	height: 50px;
	width: 100%;
`;

const RefreshBanner = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 10px;
	flex-shrink: 0;
	padding: 6px 20px;
	font-size: 12px;
	color: ${GreySidely3};
	background-color: ${BorderColor}40;
	border-bottom: 1px solid ${BorderColor};
`;

const AiWrapper = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	transition: 1s;
	width: 100%;
	height: 100%;
	overflow: hidden;
	padding: 10px 20px;
	position: relative;
`;

const ChatWrapper = styled.div<{ hasAgent: boolean }>`
	display: flex;
	flex-direction: column-reverse;
	overflow-y: auto;
	max-height: calc(100% - 50px${p => p.hasAgent ? ' - 30px' : ''});
	width: calc(100% + 20px);
	padding-right: 20px;
	gap: 10px;
	flex-shrink: 0;
`;

const Question = styled.p`
	margin: 0;
	align-self: flex-end;
	padding: 10px;
	background-color: color-mix(in oklab, ${PrimaryColor} 20%, transparent);
	border-radius: 10px 10px 0 10px;
`;

const Answer = styled.div<{ questionHeight: number, isLast: boolean, wrapperHeight?: number }>`
	align-self: flex-start;
	/* container_height - AI_ICON height - question height - gap (2 * 10px) - (padding_bottom + input_height + padding_top) */
	${p => p.isLast ? `min-height: calc(${p.wrapperHeight ?? 200}px - ${DEFAULT_AI_ICON_SIZE} - ${p.questionHeight}px - 20px - 90px);` : ''}
	flex-shrink: 0;
`;

type DotsAnimationDivProps = { opacity?: string };

const DotsAnimationDiv = styled.p<DotsAnimationDivProps>`
	margin: 0;
	${props => props.opacity ? `opacity: ${props.opacity};` : ''}
	.dot {
  		opacity: 0;
    	animation: blink 1.4s infinite;
     	width: 4px;
     }

     .dot:nth-child(2) { animation-delay: 0.2s; }
     .dot:nth-child(3) { animation-delay: 0.4s; }
     .dot:nth-child(4) { animation-delay: 0.6s; }

     @keyframes blink {
     	0%, 20% { opacity: 0; }
     	50% { opacity: 1; }
     	100% { opacity: 0; }
     }
`;


function ThinkingAnimation(props: DotsAnimationDivProps & { loadingState?: LoadingStateEnum }) {
	if (props.loadingState === LoadingStateEnum.LOADED) {
		return <DotsAnimationDiv {...props}>
			<Translate id='thinking_finished' />
		</DotsAnimationDiv>;
	}
	if (props.loadingState === LoadingStateEnum.ERROR) {
		return <DotsAnimationDiv {...props}>
			<Translate id='error' />
		</DotsAnimationDiv>;
	}
	return <DotsAnimationDiv {...props}>
		<Translate id='thinking' />
		<span className="dot">.</span>
		<span className="dot">.</span>
		<span className="dot">.</span>
	</DotsAnimationDiv>;
}

type AiIconWrapperProps = {
	circle?: boolean;
	size?: number;
	error?: boolean;
};


const AiIconWrapper = styled.div<AiIconWrapperProps>`
	${AiAnimation}
	display: flex;
	align-items: center;
	justify-content: center;
	background: ${props => props.error ? RedSidely : AiGradiant};
	border-radius: ${props => props.circle ? '50%' : '5px'};
	padding: 4px;
	width: ${props => props.size ?? DEFAULT_AI_ICON_SIZE}px;
	height: ${props => props.size ?? DEFAULT_AI_ICON_SIZE}px;
`;

export function AiIcon(props: AiIconWrapperProps) {
	return <AiIconWrapper {...props}><AiSparklingsIcon color='white' size={props.size ?? DEFAULT_AI_ICON_SIZE} /></AiIconWrapper>;
}
